import { Alert, Box, Button, Divider, Icon, IconButton, Paper, Slide, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import DropdownSelection from '../CommonComponents/DropdownSelection';
import { APICallStatus, SendAnnouncementOptions } from '../../Types/UnitFeatureTypes';
import { ArrowBack, Cancel, Close, Groups3, Home, Info, MeetingRoom, People, Person, Send } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { announcementId, fetchUnit, sendAnnouncementAPIStatus, user } from '../../Redux/selectors';
import { useLocation, useNavigate } from 'react-router-dom';
import { Actions, RootState } from '../../Redux/Reducer/rootReducer';
import { useApp } from '../../context/appContext';
import LoadingOverlay from '../CommonComponents/LoadingOverlay';

const SendAnnouncement = () => {

    const [sendSelection, setSendSelection] = useState<SendAnnouncementOptions>(SendAnnouncementOptions.ALL);
    const [unitSelection, setUnitSelection] = useState<string>('');
    const [individualSelection, setIndividualSelection] = useState<string>('');
    const [sendTransit, setSendTransit] = useState<NodeJS.Timeout | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const unitData = useSelector(fetchUnit);
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const sendAPIStatus = useSelector(sendAnnouncementAPIStatus);
    const isLoading = useSelector((state: RootState) => state.announcement.request.loading);
    const createdAnnouncementId = useSelector(announcementId);
    const { setSnackbar, setLoading } = useApp();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (sendAPIStatus === APICallStatus.SUCCESS) {
            setSnackbar(true, 'Announcement sent successfully', 'success');
            clearTimeout(sendTransit as NodeJS.Timeout);
            setSendTransit(null);
            setLoading(false);

            navigate('/announcements', { replace: true });
        }
        else if (sendAPIStatus === APICallStatus.FAILURE) {
            setSnackbar(true, 'Failed to send announcement', 'error');
            clearTimeout(sendTransit as NodeJS.Timeout);
            setSendTransit(null);
            setLoading(false);
            navigate('/announcements', { replace: true });
        }
        if (sendAPIStatus === APICallStatus.SUCCESS || sendAPIStatus === APICallStatus.FAILURE) {
            dispatch(Actions.announcement.resetStatusRequestAction());

        }
    }
    , [sendAPIStatus]);

    const validateInput = () => {
        let errors: { [key: string]: string } = {};
        let valid = true;
        if (sendSelection === SendAnnouncementOptions.SELECT_OPTION) {
            errors = { ...errors, sendSelection: 'Please select a valid option' };
            valid = false;
        }
        if (sendSelection === SendAnnouncementOptions.UNIT && unitSelection === '') {
            errors = { ...errors, unitSelection: 'Please select a unit' };
            valid = false;
        }
        if (sendSelection === SendAnnouncementOptions.INDIVIDUAL && unitSelection === '') {
            errors = { ...errors, unitSelection: 'Please select an unit' };
            valid = false;
        }
        if (sendSelection === SendAnnouncementOptions.INDIVIDUAL && individualSelection === '') {
            errors = { ...errors, individualSelection: 'Please select an individual' };
            valid = false;
        }
        setErrors(errors);
        return valid;
    };

    const handleSendAnnouncement = () => {
        if (!validateInput()) return;
        const selection = sendSelection;
        const announcement_id = location.state?.announcementId ? location.state?.announcementId : createdAnnouncementId;
        const user_ids: string[] = [];
        let requestObject: { announcement_id: string; user_ids?: string[]; sendSelection: SendAnnouncementOptions; unit_id?: string } = { announcement_id: announcement_id, sendSelection: sendSelection };

        if (selection === SendAnnouncementOptions.SELECT_OPTION) return;
        if (announcement_id === undefined) return;
        if (selection === SendAnnouncementOptions.UNIT) {
            if (unitSelection === '') return;
            let unit = unitData.find((unit) => unit.id.toString() === unitSelection);
            if (unit === undefined) return;
        }
        if (selection === SendAnnouncementOptions.INDIVIDUAL && individualSelection === '') return

        switch (selection) {
            case SendAnnouncementOptions.ALL:
            case SendAnnouncementOptions.BOARD:
                break;
            case SendAnnouncementOptions.UNIT_OWNERS:
                user_ids.push(...unitData.map((unit) => unit.owner?.id?.toString() || ''));
                break;
            case SendAnnouncementOptions.INDIVIDUAL:
                user_ids.push(individualSelection);
                requestObject = { announcement_id: announcement_id, user_ids, sendSelection };
                break;
            case SendAnnouncementOptions.UNIT:
                requestObject = { announcement_id: announcement_id, sendSelection, unit_id: unitSelection };
                if (unitSelection === '') return;
                let unitDataOne = unitData.find((unit) => unit.id.toString() === unitSelection);
                if (unitDataOne === undefined) return;
                user_ids.push(unitDataOne.owner?.id?.toString() || '');
                user_ids.push(...(unitDataOne.tenants?.map((tenant) => tenant.id.toString()) || []));
                break;
            default:
                break
        }

        if (sendTransit === null) {
            timeoutRef.current = setTimeout(() => {
                setSendTransit(null);
                setLoading(true);
                dispatch(Actions.announcement.sendAnnouncementRequestAction.request({ ...requestObject, user_ids }));
            }, 5000);
            setSendTransit(timeoutRef.current);
        }
    };

    const handleUndoSend = () => {
        if (sendTransit) {
            timeoutRef.current = null;
            clearTimeout(sendTransit);
            setSendTransit(null);
        }
    };

    return (
        <>
        <LoadingOverlay isLoading={isLoading} />
        <Snackbar
            open={sendTransit != null}
            autoHideDuration={5000}
            onClose={handleUndoSend}
            message="Sending Announcement"
            slots={{transition: (props) => <Slide {...props} direction="up" />}}
            action={<Button color="secondary" size="small" onClick={handleUndoSend}>UNDO</Button>}
            sx={{
            '& .MuiSnackbarContent-root': {
                backgroundColor: '#fff',
                color: '#000',
                boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)',
            },
            }}
        />
            
        <Paper elevation={5} sx={{ padding: 6, borderRadius: 3 }}>
            <Stack direction='row' spacing={2} alignItems='center'>
                <Tooltip title="Go Back"><IconButton onClick={()=>navigate(-1)}><ArrowBack /></IconButton></Tooltip>
                <Typography variant="h5" sx={{ mt: 1, mb: 2 }}>Send Announcement</Typography>
            </Stack>
        <DropdownSelection
            dropDownLabel='Send To'
            showHeader={false}
            labelSx={{mt:1.5}}
            showError={!!errors.sendSelection}
            helperText={errors.sendSelection}
            currentValue={sendSelection}
            selectionValues={[
                { value: SendAnnouncementOptions.SELECT_OPTION, label: 'Select Option', color: 'grey', icon: Info },
                { value: SendAnnouncementOptions.ALL, label: 'All', color: 'grey', icon: People },
                { value: SendAnnouncementOptions.BOARD, label: 'Board Only', color: 'grey', icon: Groups3 },
                { value: SendAnnouncementOptions.INDIVIDUAL, label: 'Individual', color: 'grey', icon: Person },
                { value: SendAnnouncementOptions.UNIT_OWNERS, label: 'Unit Owners', color: 'grey', icon: Home },
                { value: SendAnnouncementOptions.UNIT, label: 'Unit', color: 'grey', icon: MeetingRoom },
            ]}
            variant='outlined'
            sxObject={{ mb: 2, mt: 1.5}}
            handleOnChange={(e) => {setSendSelection(e.target.value as SendAnnouncementOptions); setUnitSelection(''); setIndividualSelection(''); setErrors({})}}
        />

        {(SendAnnouncementOptions.UNIT === sendSelection || SendAnnouncementOptions.INDIVIDUAL === sendSelection) && (
        <DropdownSelection
            dropDownLabel='Unit'
            showHeader={false}
            labelSx={{mt:1.5}}
            currentValue={unitSelection}
            selectionValues={unitData.map((unit) => ({
                value: unit.id.toString(),
                label: unit.unit_number.toString(),
                color: 'grey',
                icon: MeetingRoom,
            }))}
            showError={!!errors.unitSelection}
            helperText={errors.unitSelection}
            variant='outlined'
            sxObject={{ mb: 2, mt: 1.5}}
            handleOnChange={(e) => {setUnitSelection(e.target.value as string); setErrors({}); setIndividualSelection('')}}
        />
        )}

        {SendAnnouncementOptions.INDIVIDUAL === sendSelection && unitSelection !== '' && (
        <DropdownSelection
            dropDownLabel='Individual'
            showHeader={false}
            labelSx={{mt:1.5}}
            currentValue={individualSelection}
            showError={!!errors.individualSelection}
            helperText={errors.individualSelection}
            selectionValues={
            (unitData.find((unit) => unit.id.toString() === unitSelection)
                ? [{
                    value: unitData.find((unit) => unit.id.toString() === unitSelection)?.owner?.id.toString() || '',
                    label: (unitData.find((unit) => unit.id.toString() === unitSelection)?.owner?.name || '') + ' (Owner)',
                    color: 'grey',
                    icon: Person,
                }]
                : []).concat(
                ...(unitData.find((unit) => unit.id.toString() === unitSelection)?.tenants?.map((tenant) => ({
                value: tenant.id.toString(),
                label: tenant.name + ' (Tenant)',
                color: 'grey',
                icon: Person,
                })) || []))
            }
            variant='outlined'
            sxObject={{ mb: 2, mt: 1.5}}
            handleOnChange={(e) => {setIndividualSelection(e.target.value as string); setErrors({})}}
        />
        )}
        <Box sx={{ mt:2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
                variant='contained'
                onClick={handleSendAnnouncement}
            >
                Send <Send sx={{ml:2}} />
            </Button>
        </Box>
    </Paper>
    </>
    );
};

export default SendAnnouncement;